import { useMemo } from 'react';
import clsx from 'clsx';
import { PermissionMatrix } from '../roles/PermissionMatrix.jsx';

const MODES = [
  { key: 'role', label: 'افتراضي من الدور', hint: 'يرث المستخدم صلاحيات الدور المحدّد تلقائياً.' },
  { key: 'custom', label: 'تخصيص الصلاحيات', hint: 'تعديل دقيق لكل صلاحية على حدة لهذا المستخدم.' }
];

function flatPerms(groups) {
  const all = [];
  for (const g of groups || []) {
    for (const p of g.perms || g.permissions || []) {
      all.push(p.key || p);
    }
  }
  return all;
}

export function PermissionEditor({
  mode,
  onModeChange,
  selected,
  onSelectedChange,
  groups,
  role,
  loadingGroups,
  loadingUserPerms,
  disabled
}) {
  const safeSelected = Array.isArray(selected) ? selected : [];

  const rolePerms = useMemo(() => {
    if (!role) return [];
    return Array.isArray(role.permissions) ? role.permissions : [];
  }, [role]);

  const hasWildcard = rolePerms.includes('*');
  const totalCount = useMemo(() => flatPerms(groups).length, [groups]);
  const effectiveCount = mode === 'role'
    ? (hasWildcard ? totalCount : rolePerms.length)
    : safeSelected.length;

  function toggle(key) {
    if (disabled) return;
    onSelectedChange(
      safeSelected.includes(key) ? safeSelected.filter((p) => p !== key) : [...safeSelected, key]
    );
  }

  function applyRoleDefaults() {
    if (disabled) return;
    if (hasWildcard) {
      onSelectedChange(flatPerms(groups));
    } else {
      onSelectedChange([...rolePerms]);
    }
  }

  function selectAll() {
    if (disabled) return;
    onSelectedChange(flatPerms(groups));
  }

  function clearAll() {
    if (disabled) return;
    onSelectedChange([]);
  }

  return (
    <div className="border border-line rounded-[12px] bg-paper-2/40">
      <div className="px-3.5 py-2.5 border-b border-line flex items-center gap-2">
        <span className="text-[14px]">🛡️</span>
        <h4 className="m-0 text-[13px] font-bold flex-1">الصلاحيات</h4>
        <span className="text-[11px] text-ink-3">
          مفعّل: <b className="text-ink">{effectiveCount}</b> / {totalCount}
        </span>
      </div>

      <div className="p-3.5 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              disabled={disabled}
              onClick={() => onModeChange(m.key)}
              className={clsx(
                'text-right p-2.5 rounded-[10px] border transition-colors',
                mode === m.key
                  ? 'border-orange bg-[#FFFCF5]'
                  : 'border-line bg-white hover:bg-paper-2',
                disabled && 'opacity-60 cursor-not-allowed'
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={clsx(
                    'w-3.5 h-3.5 rounded-full border-2 flex-none',
                    mode === m.key ? 'border-orange' : 'border-ink-3'
                  )}
                >
                  {mode === m.key ? (
                    <span className="block w-1.5 h-1.5 rounded-full bg-orange m-[3px]" />
                  ) : null}
                </span>
                <span className="text-[13px] font-bold">{m.label}</span>
              </div>
              <div className="text-[11px] text-ink-3 mt-1">{m.hint}</div>
            </button>
          ))}
        </div>

        {mode === 'role' ? (
          <div className="p-3 rounded-[10px] bg-white border border-line text-[12.5px] text-ink-2">
            {role ? (
              <>
                سيرث المستخدم{' '}
                <b style={{ color: role.color || undefined }}>{role.name}</b>{' '}
                — {hasWildcard ? 'كل الصلاحيات (مدير عام)' : `${rolePerms.length} صلاحية`}.
              </>
            ) : (
              <span className="text-ink-3">اختر دوراً أولاً لمعاينة صلاحياته الافتراضية.</span>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn"
                style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={applyRoleDefaults}
                disabled={disabled || !role}
                title="استبدال التحديد الحالي بصلاحيات الدور"
              >
                ↻ تطبيق صلاحيات الدور
              </button>
              <button
                type="button"
                className="btn"
                style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={selectAll}
                disabled={disabled}
              >
                تحديد الكل
              </button>
              <button
                type="button"
                className="btn"
                style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={clearAll}
                disabled={disabled}
              >
                إفراغ
              </button>
            </div>

            {loadingGroups || loadingUserPerms ? (
              <div className="text-center py-6 text-ink-3 text-sm">جاري تحميل الصلاحيات...</div>
            ) : (
              <div className="max-h-[340px] overflow-y-auto p-1">
                <PermissionMatrix
                  groups={groups}
                  selected={safeSelected}
                  onToggle={toggle}
                  disabled={disabled}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
